import moment from "moment";

/**
 * Builds the user-facing download name of an exported report from the filters
 * the user actually filled. Only the values are written (no filter labels) and
 * empty filters are skipped, so the name only carries what the user chose:
 *
 *   مستندات الجهد الخارجي - ذاتي - دائرة بلدية الكاظمية - 01-09-2026 الى 28-09-2026
 *
 * Works for the transactions summary page and both detailed pages (external /
 * internal effort), which share the same filter object shape.
 */

// Characters Windows / macOS refuse inside a file name.
const FORBIDDEN_CHARS = /[\\/:*?"<>|]/g;
// Keep under Windows' 260-character full-path limit (Downloads folder + name +
// extension) so the browser never has to mangle the name.
const DEFAULT_MAX_LENGTH = 220;
// A single very long value (e.g. an account name) must not crowd out the other filters.
const MAX_VALUE_LENGTH = 60;
const DATE_FORMAT = "DD-MM-YYYY";

export const sanitizeFileNamePart = (value) => {
  const clean = String(value ?? "")
    .replace(FORBIDDEN_CHARS, " ")
    .replace(/\s+/g, " ")
    .trim();
  return clean.length > MAX_VALUE_LENGTH ? clean.slice(0, MAX_VALUE_LENGTH - 1).trim() + "…" : clean;
};

const dateOnly = (value) => {
  if (!value) return "";
  const m = moment(value);
  return m.isValid() ? m.format(DATE_FORMAT) : sanitizeFileNamePart(value);
};

const isFilled = (value) =>
  value !== null && value !== undefined && String(value).trim() !== "";

const namesOf = (list) =>
  (Array.isArray(list) ? list : [])
    .map((item) => item?.name)
    .filter(Boolean)
    .join("، ");

/**
 * Returns the ordered list of filled filter values (without the period).
 * Order follows the request: vehicle, ownership, effort type, municipality,
 * mizan, loading point, groups, unloading point, weight range.
 */
export const buildFilterNameParts = (filters = {}) => {
  const parts = [];
  const push = (value) => {
    const clean = sanitizeFileNamePart(value);
    if (clean) parts.push(clean);
  };

  push(filters.unit?.name);
  push(filters.account?.name);
  if (isFilled(filters.is_governmental)) {
    push(String(filters.is_governmental) === "1" ? "ذاتي" : "مؤجر");
  }
  push(filters.municipality?.name || filters.municipality?.municipality_name);
  push(filters.mizan_selected?.name);
  push(filters.sourceGeofence?.name);
  push(namesOf(filters.groups_selected));
  push(filters.destinationGeofence?.name);

  const hasMin = isFilled(filters.min_weight);
  const hasMax = isFilled(filters.max_weight);
  if (hasMin && hasMax) push(`${filters.min_weight} الى ${filters.max_weight} طن`);
  else if (hasMin) push(`من ${filters.min_weight} طن`);
  else if (hasMax) push(`الى ${filters.max_weight} طن`);

  return parts;
};

/** "01-09-2026 الى 28-09-2026" (or the single bound), empty when no period is set. */
export const buildPeriodPart = (filters = {}) => {
  const from = dateOnly(filters.creation_date_from);
  const to = dateOnly(filters.creation_date_to);
  if (from && to) return `${from} الى ${to}`;
  if (from) return `من ${from}`;
  if (to) return `الى ${to}`;
  return "";
};

/**
 * `<baseTitle> - <value> - <value> ... - <period>` (no extension). The period
 * is always kept (it is the last part); any other part that would push the
 * name past `maxLength` is skipped so the download never fails on an
 * over-long name.
 */
export const buildReportFileName = (baseTitle, filters = {}, options = {}) => {
  const maxLength = options.maxLength || DEFAULT_MAX_LENGTH;
  const periodPart = buildPeriodPart(filters);
  const reserved = periodPart ? periodPart.length + 3 : 0;

  let name = String(baseTitle ?? "").replace(FORBIDDEN_CHARS, " ").replace(/\s+/g, " ").trim();
  buildFilterNameParts(filters).forEach((part) => {
    const candidate = `${name} - ${part}`;
    if (candidate.length + reserved <= maxLength) name = candidate;
  });
  if (periodPart) name = `${name} - ${periodPart}`;
  return name;
};

const reportFileName = { sanitizeFileNamePart, buildFilterNameParts, buildPeriodPart, buildReportFileName };
export default reportFileName;
